import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Leaf,
  ArrowRight,
  ChartLineUp,
  ShieldCheck,
  Certificate,
  Scales,
  Coins,
  ClipboardText,
  UsersFour,
  Globe,
  CheckCircle,
  Buildings,
  Factory,
  Handshake,
  Sparkle,
} from "@phosphor-icons/react";

const FEATURES = [
  {
    icon: ChartLineUp,
    title_tr: "ISO 14064 & GHG Protokolü",
    title_en: "ISO 14064 & GHG Protocol",
    body_tr: "Kapsam 1, 2 ve 3 emisyonlarını uluslararası standartlara uygun hesaplayın; sekiz sera gazının tamamı için GWP faktörleri hazır.",
    body_en: "Compute Scope 1, 2 and 3 emissions to international standards; all eight GHGs supported with baked-in GWP factors.",
  },
  {
    icon: Scales,
    title_tr: "CBAM AB Sınır Karbon",
    title_en: "CBAM EU Border Carbon",
    body_tr: "Çeyreklik CBAM ödemenizi hesaplayın, AB CBAM Registry formatında PDF & XML export edin. 6 sektör + CN kod desteği.",
    body_en: "Compute your quarterly CBAM certificate liability, export PDF & XML in EU CBAM Registry format. Six sectors + CN codes.",
  },
  {
    icon: ClipboardText,
    title_tr: "ESRS · GRI · SBTi Uyumlu",
    title_en: "ESRS · GRI · SBTi Compatible",
    body_tr: "CSRD kapsamında ESRS E1-E5 + S1-S4 + G1, ve GRI 2/200/300/400 standart açıklamaları. SBTi Paris uyumlu hedefler.",
    body_en: "CSRD-ready ESRS E1-E5 + S1-S4 + G1, plus GRI 2/200/300/400 disclosures. SBTi Paris-aligned targets.",
  },
  {
    icon: Coins,
    title_tr: "Global Karbon Vergisi",
    title_en: "Global Carbon Tax",
    body_tr: "12 farklı jurisdiction — AB ETS, İsveç, İsviçre, Türkiye pilot, Kanada, Kaliforniya — hepsi tek panelde karşılaştırın.",
    body_en: "12 jurisdictions — EU ETS, Sweden, Switzerland, Türkiye pilot, Canada, California — compared side-by-side.",
  },
  {
    icon: UsersFour,
    title_tr: "Danışman Müşteri Portalı",
    title_en: "Consultant Client Portal",
    body_tr: "Enasuspro® danışmanlarının tüm müşterilerini tek panelden takip eden portföy yönetimi; 5 adımlı onboarding checklist.",
    body_en: "Portfolio management for Enasuspro® consultants tracking all clients from one dashboard; 5-step onboarding checklist.",
  },
  {
    icon: ShieldCheck,
    title_tr: "Bağımsız Doğrulama",
    title_en: "Independent Verification",
    body_tr: "ISO 14064-3 / ISAE 3000 doğrulayıcıları için audit trail, onay iş akışı, denetlenebilir kayıt izi.",
    body_en: "Audit trail for ISO 14064-3 / ISAE 3000 verifiers, approval workflow, immutable log of every change.",
  },
];

const TIERS = [
  {
    name: "Starter",
    price_eur: 99,
    highlight: false,
    for_tr: "KOBİ · tek tesis · ilk raporunu üreten",
    for_en: "SMB · single facility · producing first report",
    bullets_tr: ["1 kuruluş", "1 tesis", "Sınırsız kayıt & hedef", "ISO 14064 raporu PDF", "TR/EN dil"],
    bullets_en: ["1 organization", "1 facility", "Unlimited entries & targets", "ISO 14064 PDF report", "TR/EN language"],
  },
  {
    name: "Pro",
    price_eur: 299,
    highlight: true,
    for_tr: "AB'ye ihracat yapan orta ölçekli üretici",
    for_en: "Mid-size manufacturer exporting to EU",
    bullets_tr: [
      "10 kuruluş, 25 tesis",
      "CBAM PDF + XML export",
      "ESRS E1-E5 + S1-S4 + G1",
      "GRI standart raporlaması",
      "Karbon vergisi simülatörü",
      "Onay iş akışı",
    ],
    bullets_en: [
      "10 orgs, 25 facilities",
      "CBAM PDF + XML export",
      "ESRS E1-E5 + S1-S4 + G1",
      "GRI standards reporting",
      "Carbon tax simulator",
      "Approval workflow",
    ],
  },
  {
    name: "Enterprise",
    price_eur: null,
    highlight: false,
    for_tr: "Danışmanlık firmaları · çok müşterili portföyler",
    for_en: "Consulting firms · multi-client portfolios",
    bullets_tr: [
      "Sınırsız müşteri org",
      "Müşteri portalı & onboarding",
      "Beyaz etiket (kendi markanız)",
      "API erişimi",
      "Öncelikli destek",
      "SLA & SSO (istek üzerine)",
    ],
    bullets_en: [
      "Unlimited client orgs",
      "Client portal & onboarding",
      "White-label (your brand)",
      "API access",
      "Priority support",
      "SLA & SSO (on request)",
    ],
  },
];

const USE_CASES = [
  {
    icon: Factory,
    title_tr: "Türk çelik üreticisi · AB'ye ihracat",
    title_en: "Turkish steel manufacturer · EU export",
    body_tr: "Q1-Q4 CBAM beyanlarınızı Enasuspro® ile hesaplayın, XML olarak AB CBAM Registry'ye doğrudan yükleyin. €2,550-€100,000+ ödeme riskinizi görün.",
    body_en: "Compute your Q1-Q4 CBAM declarations with Enasuspro®, upload the XML directly to the EU CBAM Registry. See your €2,550-€100,000+ liability upfront.",
  },
  {
    icon: Buildings,
    title_tr: "500+ çalışanlı işletme · CSRD zorunluluğu",
    title_en: "500+ employee enterprise · CSRD mandate",
    body_tr: "2027'den itibaren ESRS E1-E5 + S1-S4 + G1 açıklamaları zorunlu. Enasuspro® ile tüm datapointleri şimdiden hazırlayın, denetçinize teslim edin.",
    body_en: "From 2027 onwards, ESRS E1-E5 + S1-S4 + G1 disclosures are mandatory. Prepare all datapoints ahead of time with Enasuspro® and hand over to your auditor.",
  },
  {
    icon: Handshake,
    title_tr: "Sürdürülebilirlik danışmanlığı firması",
    title_en: "Sustainability consulting firm",
    body_tr: "Her müşteriniz için ayrı kuruluş, ayrı ekip, ayrı raporlar. Portföy panelinden 5-adım onboarding ilerlemesini takip edin.",
    body_en: "Separate org, team, and reports for each client. Track 5-step onboarding progress from the portfolio dashboard.",
  },
];

const STANDARDS = [
  "ISO 14064-1", "ISO 14064-3", "GHG Protocol", "SBTi", "CDP",
  "CSRD", "ESRS", "GRI", "IFRS S2", "TCFD",
  "CBAM", "EU ETS", "ISAE 3000", "REACH", "IED",
];

export default function Landing() {
  const { lang, setLang } = useLang();
  const { user } = useAuth();

  const isTR = lang === "tr";
  const T = (tr, en) => (isTR ? tr : en);

  return (
    <div className="min-h-screen bg-background grain-bg relative z-0">
      {/* Header */}
      <header className="border-b border-border sticky top-0 bg-background/80 backdrop-blur-xl z-30">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-brand">
            <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
              <Leaf size={20} weight="fill" />
            </div>
            <div>
              <div className="font-display font-semibold">Enasuspro® Carbon Ledger</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Sustainability Coaching · ISO 14064</div>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-6 text-sm">
            <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-features">{T("Özellikler", "Features")}</a>
            <a href="#use-cases" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-usecases">{T("Kullanım Alanları", "Use Cases")}</a>
            <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-pricing">{T("Fiyatlar", "Pricing")}</a>
            <a href="#contact" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-contact">{T("İletişim", "Contact")}</a>
            <Link to="/blog" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-blog">{T("Blog", "Blog")}</Link>
          </div>
          <div className="flex items-center gap-3">
            <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
              <button onClick={() => setLang("tr")} className={`px-3 py-1.5 font-mono-data ${lang === "tr" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`} data-testid="landing-lang-tr">TR</button>
              <button onClick={() => setLang("en")} className={`px-3 py-1.5 font-mono-data ${lang === "en" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`} data-testid="landing-lang-en">EN</button>
            </div>
            {user ? (
              <Link to="/app" data-testid="landing-cta-app"><Button size="sm">{T("Panele Git", "Go to App")}<ArrowRight size={14} className="ml-1" /></Button></Link>
            ) : (
              <>
                <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="landing-login">{T("Giriş", "Login")}</Link>
                <Link to="/register" data-testid="landing-cta-register"><Button size="sm">{T("Ücretsiz Başla", "Start Free")}<ArrowRight size={14} className="ml-1" /></Button></Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-border relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center relative z-10">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-mono-data uppercase tracking-widest mb-6">
                <Sparkle size={12} weight="fill" />
                {T("v2.1 · CBAM · ESRS · GRI hepsi bir platformda", "v2.1 · CBAM · ESRS · GRI · all in one platform")}
              </div>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.05] tracking-tight" data-testid="hero-title">
                {T("Karbon uyumluluğu ", "Carbon compliance ")}
                <span className="text-primary">
                  {T("tek platformda.", "in one platform.")}
                </span>
              </h1>
              <p className="text-lg text-muted-foreground mt-6 max-w-2xl">
                {T(
                  "Enasuspro® Carbon Ledger; ISO 14064, GHG Protokolü, CBAM, ESRS, GRI ve SBTi standartlarını tek yerde birleştirir. Türkiye'den AB'ye ihracat yapan üreticiler ve sürdürülebilirlik danışmanları için tasarlandı.",
                  "Enasuspro® Carbon Ledger unifies ISO 14064, GHG Protocol, CBAM, ESRS, GRI and SBTi standards in a single place. Built for manufacturers exporting from Türkiye to the EU and their sustainability consultants.",
                )}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={user ? "/app" : "/register"} data-testid="hero-cta">
                  <Button size="lg" className="text-base">
                    {user ? T("Panele Git", "Go to App") : T("Ücretsiz Başla", "Start Free")}
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
                <a href="#pricing">
                  <Button size="lg" variant="outline" className="text-base" data-testid="hero-cta-secondary">
                    {T("Planları Gör", "See Plans")}
                  </Button>
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground font-mono-data uppercase tracking-widest">
                <span className="flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-primary" />{T("Kredi kartı gerekmez", "No credit card")}</span>
                <span className="flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-primary" />{T("14 gün deneme", "14-day trial")}</span>
                <span className="flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-primary" />TR + EN</span>
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-primary/5 rounded-2xl blur-2xl" />
                <div className="relative bg-card border border-border rounded-lg p-6 shadow-lg">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data mb-4">
                    Q1 2026 · CBAM
                  </div>
                  <div className="font-mono-data text-sm space-y-2">
                    <div className="flex justify-between"><span>500 t · Iron & Steel</span><span className="text-muted-foreground">Türkiye</span></div>
                    <div className="flex justify-between"><span>Embedded</span><span>1,200.000 tCO₂e</span></div>
                    <div className="flex justify-between"><span>Gross</span><span>€102,000.00</span></div>
                    <div className="flex justify-between text-muted-foreground"><span>Origin credit</span><span>−€0.00</span></div>
                    <div className="flex justify-between text-muted-foreground"><span>Phase-in factor</span><span>2.5%</span></div>
                    <div className="border-t border-border my-2" />
                    <div className="flex justify-between text-lg font-display font-semibold">
                      <span>CBAM {T("ödenecek", "payable")}</span>
                      <span className="text-primary">€2,550.00</span>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <div className="text-[10px] font-mono-data px-2 py-1 rounded-sm bg-primary/10 text-primary">PDF</div>
                    <div className="text-[10px] font-mono-data px-2 py-1 rounded-sm bg-primary/10 text-primary">XML</div>
                    <div className="text-[10px] font-mono-data px-2 py-1 rounded-sm bg-secondary text-muted-foreground">EU Registry ready</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Standards ticker */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data mb-4 text-center">
            {T("Desteklenen standartlar", "Supported standards")}
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {STANDARDS.map((s) => (
              <div key={s} className="text-xs font-mono-data px-3 py-1.5 bg-secondary rounded-sm border border-border">
                {s}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section id="features" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{T("Özellikler", "Features")}</div>
            <h2 className="font-display text-4xl md:text-5xl font-semibold mt-2">
              {T("Bir kuruluşun sürdürülebilirlik bilançosuna", "Everything a company needs")}
              <br />
              <span className="text-primary">{T("ihtiyacı olan her şey.", "for a sustainability balance sheet.")}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title_en} className="border border-border rounded-md p-6 hover:border-primary/50 transition-colors group">
                  <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <Icon size={20} weight="duotone" />
                  </div>
                  <h3 className="font-display text-lg font-semibold">{T(f.title_tr, f.title_en)}</h3>
                  <p className="text-sm text-muted-foreground mt-2">{T(f.body_tr, f.body_en)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section id="use-cases" className="border-b border-border bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{T("Kimler kullanıyor", "Who uses it")}</div>
            <h2 className="font-display text-4xl md:text-5xl font-semibold mt-2">
              {T("Üç somut senaryo.", "Three concrete scenarios.")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {USE_CASES.map((u) => {
              const Icon = u.icon;
              return (
                <div key={u.title_en} className="bg-card border border-border rounded-md p-6">
                  <div className="w-12 h-12 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-4">
                    <Icon size={24} weight="duotone" />
                  </div>
                  <h3 className="font-display text-lg font-semibold">{T(u.title_tr, u.title_en)}</h3>
                  <p className="text-sm text-muted-foreground mt-3">{T(u.body_tr, u.body_en)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{T("Fiyatlandırma", "Pricing")}</div>
            <h2 className="font-display text-4xl md:text-5xl font-semibold mt-2">
              {T("Şeffaf. Ölçeklenebilir.", "Transparent. Scalable.")}
            </h2>
            <p className="text-muted-foreground mt-4">
              {T(
                "İlk 14 gün ücretsiz. Kredi kartı gerekmez. Aylık faturalama Enasuspro® tarafından manuel kesilir.",
                "First 14 days free. No credit card required. Monthly invoicing handled manually by Enasuspro®.",
              )}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {TIERS.map((tier) => (
              <div
                key={tier.name}
                className={`rounded-md p-6 border ${tier.highlight ? "border-primary bg-primary/5 shadow-lg md:scale-105" : "border-border"}`}
                data-testid={`pricing-${tier.name.toLowerCase()}`}
              >
                {tier.highlight && (
                  <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary text-primary-foreground rounded-sm text-[10px] uppercase tracking-widest font-mono-data mb-3">
                    <Sparkle size={10} weight="fill" />
                    {T("En Popüler", "Most Popular")}
                  </div>
                )}
                <div className="font-display text-2xl font-semibold">{tier.name}</div>
                <div className="text-xs text-muted-foreground mt-1">{T(tier.for_tr, tier.for_en)}</div>
                <div className="mt-4 flex items-baseline gap-1">
                  {tier.price_eur ? (
                    <>
                      <span className="font-display text-4xl font-semibold">€{tier.price_eur}</span>
                      <span className="text-muted-foreground text-sm">/{T("ay", "mo")}</span>
                    </>
                  ) : (
                    <span className="font-display text-3xl font-semibold">{T("Özel", "Custom")}</span>
                  )}
                </div>
                <div className="mt-6 space-y-2">
                  {T(tier.bullets_tr, tier.bullets_en).map((b) => (
                    <div key={b} className="flex gap-2 text-sm">
                      <CheckCircle size={16} weight="fill" className="text-primary flex-shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
                <Link to={user ? "/app" : "/register"} className="block mt-6">
                  <Button className="w-full" variant={tier.highlight ? "default" : "outline"}>
                    {tier.price_eur ? T("Bu planla başla", "Start with this plan") : T("İletişime geç", "Contact us")}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="border-b border-border bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 py-16 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl md:text-4xl font-semibold">
              {T("İlk ISO 14064 raporunuz 30 dakikada.", "Your first ISO 14064 report in 30 minutes.")}
            </h2>
            <p className="mt-2 opacity-90">
              {T(
                "Enasuspro® Sustainability Coaching ekibi size onboarding sırasında eşlik eder.",
                "Enasuspro® Sustainability Coaching team walks with you through onboarding.",
              )}
            </p>
          </div>
          <Link to={user ? "/app" : "/register"} data-testid="cta-banner-btn">
            <Button size="lg" variant="secondary" className="text-base">
              {user ? T("Panele Git", "Go to App") : T("Hemen Başla", "Get Started")}
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-background">
        <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
                <Leaf size={20} weight="fill" />
              </div>
              <div>
                <div className="font-display font-semibold">Enasuspro® Carbon Ledger</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-widest">by Enasuspro® Sustainability Coaching</div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              {T(
                "Türkiye merkezli bir sürdürülebilirlik danışmanlığı firması olarak, KOBİ'lerden büyük kuruluşlara ISO 14064, GHG Protokolü, CBAM ve CSRD uyumluluğunda çözüm ortağıyız.",
                "As a Türkiye-based sustainability consultancy, we partner with SMBs to large enterprises on ISO 14064, GHG Protocol, CBAM and CSRD compliance.",
              )}
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data mb-3">{T("Ürün", "Product")}</div>
            <ul className="text-sm space-y-2">
              <li><a href="#features" className="hover:text-primary transition-colors">{T("Özellikler", "Features")}</a></li>
              <li><a href="#pricing" className="hover:text-primary transition-colors">{T("Fiyatlar", "Pricing")}</a></li>
              <li><Link to="/login" className="hover:text-primary transition-colors">{T("Giriş", "Login")}</Link></li>
              <li><Link to="/register" className="hover:text-primary transition-colors">{T("Ücretsiz Deneme", "Free Trial")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data mb-3">{T("İletişim", "Contact")}</div>
            <ul className="text-sm space-y-2">
              <li><a href="mailto:info@enasuspro.com" className="hover:text-primary transition-colors">info@enasuspro.com</a></li>
              <li><a href="mailto:coaching@enasuspro.com" className="hover:text-primary transition-colors">coaching@enasuspro.com</a></li>
              <li className="text-muted-foreground">{T("Türkiye · Global", "Türkiye · Global")}</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-muted-foreground font-mono-data">
            <span>© {new Date().getFullYear()} Enasuspro® Sustainability Coaching · Enasuspro® Carbon Ledger · ISO 14064-1 / GHG Protocol</span>
            <span>v2.1</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
